"use client";

import type React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useControllableState } from "../../hooks/use-controllable-state";
import { usePrefersReducedMotion } from "../../hooks/use-prefers-reduced-motion";
import type { Omittable } from "../../lib/props";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import { DrawerHeader } from "./drawer";
import { Icon } from "./icon";
import { durationToken } from "../../lib/motion-tokens";

/** The slide's length is the `slow` step (`duration-slow` on the page):
 *  shorter than the popup's own 450ms travel, since a page crosses the
 *  popup's width, not the screen's height, and answers a press the reader is
 *  waiting on. The timers below read the same token. */
const pageTransitionMs = () => durationToken("slow");

/** How long an entering page waits for its first animation frame before it
 *  slides regardless: a throttled tab may hold its frames for seconds. */
const ENTER_FRAME_FALLBACK_MS = 50;

/** How much longer than the slide an ending page may wait for its
 *  `transitionend` before it is unmounted regardless — a tab hidden mid-slide
 *  never fires one. */
const TRANSITION_END_GRACE_MS = 100;

type DrawerPagesContextValue = {
  /** The pushed page on top, or `null` while the root shows. */
  page: string | null;
  /** Push a page over the root. Pushing over another page replaces it. */
  open: (page: string) => void;
  /** Pop back to the root. */
  back: () => void;
  /** Where a page's DOM lands: the layer over the root. Null for the first
   *  commit, before the layer has mounted. */
  host: HTMLDivElement | null;
};

const DrawerPagesContext: React.Context<DrawerPagesContextValue | null> =
  createContext<DrawerPagesContextValue | null>(null);

/**
 * The page stack a `DrawerPage` in this subtree lives in, or `null` outside
 * one. A list that drills into its rows on one surface and pops them open
 * beside itself on another asks this to learn which it is on.
 */
export function useDrawerPages(): DrawerPagesContextValue | null {
  return useContext(DrawerPagesContext);
}

type DrawerPagesProps = Omittable<{
  /** The page on top, controlled. `null` shows the root. */
  page?: string | null;
  defaultPage?: string | null;
  onPageChange?: (page: string | null) => void;
  /** The root page: the drawer's own header and panel. */
  children?: React.ReactNode;
  className?: string;
}>;

/**
 * A page stack inside one drawer popup: the root page, and one page at a
 * time pushed over it from the trailing edge, headed by its own back chevron,
 * title and trailing action.
 *
 * ONE popup, not a nested drawer. A second drawer over the first is a second
 * layer — its own scrim, its own grab bar, the parent scaling back behind it
 * — for what is one task the reader is one row deeper into. Drilling in keeps
 * the popup, its bar and its swipe; only the content under the bar changes,
 * the way a phone's settings app descends a list.
 *
 * The root stays MOUNTED under a pushed page: its state (a search query, a
 * scroll offset) survives the round trip, and a value the page changes reads
 * back on the root's own row the moment the page pops. While covered the
 * root is `inert`, so focus and assistive technology see only the page on
 * top. Pushing stores the focused element and popping returns focus to it,
 * so a keyboard lands back on the row it drilled from.
 *
 * Pages render wherever their data lives — inside the root's own tree — and
 * PORTAL their DOM into the layer over the root, so a list can declare the
 * pages its rows open without the popup knowing what they contain.
 *
 * The popup needs a definite height for the layer to fill: snap points, or
 * `fill` on `DrawerPopup`. A content-sized popup gives the layer the root's
 * height only.
 */
export function DrawerPages({
  page: pageProp,
  defaultPage = null,
  onPageChange,
  children,
  className,
}: DrawerPagesProps): React.ReactElement {
  const [page, setPage] = useControllableState<string | null>({
    prop: pageProp,
    defaultProp: defaultPage,
    onChange: onPageChange,
  });
  const [host, setHost] = useState<HTMLDivElement | null>(null);

  const open = useCallback((next: string) => setPage(next), [setPage]);
  const back = useCallback(() => setPage(null), [setPage]);

  // Focus round trip: the element focused when the first page was pushed
  // takes focus back when the stack returns to the root. Replacing one page
  // with another keeps the original element — the reader came from the root.
  const returnFocusTo = useRef<HTMLElement | null>(null);
  const wasCovered = useRef(page !== null);
  useEffect(() => {
    const covered = page !== null;
    if (covered && !wasCovered.current) {
      returnFocusTo.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
    } else if (!covered && wasCovered.current) {
      const target = returnFocusTo.current;
      returnFocusTo.current = null;
      if (target?.isConnected) target.focus({ preventScroll: true });
    }
    wasCovered.current = covered;
  }, [page]);

  const context = useMemo<DrawerPagesContextValue>(
    () => ({ back, host, open, page }),
    [back, host, open, page],
  );
  const covered = page !== null;

  return (
    <DrawerPagesContext.Provider value={context}>
      <div
        className={cn("relative flex min-h-0 flex-1 flex-col", className)}
        data-slot="drawer-pages"
      >
        {/* The root recedes a quarter of the way and fades as a page slides
            over it, and comes back the same way. `inert` while covered: the
            page on top is the only thing to press, tab to or read. */}
        <div
          className="flex min-h-0 flex-1 flex-col transition-[translate,opacity] duration-slow ease-drawer data-covered:-translate-x-1/4 data-covered:opacity-0 motion-reduce:transition-none"
          data-covered={covered || undefined}
          data-slot="drawer-pages-root"
          inert={covered}
        >
          {children}
        </div>
        {/* The layer pages portal into. Empty, it must not stand between the
            pointer and the root under it. */}
        <div
          className="absolute inset-0 flex flex-col empty:pointer-events-none"
          data-slot="drawer-pages-layer"
          ref={setHost}
        />
      </div>
    </DrawerPagesContext.Provider>
  );
}

/** A pushed page's life: it enters from the trailing edge, stands, ends
 *  back out and unmounts. */
type PagePhase = "closed" | "entering" | "open" | "ending";

type DrawerPageProps = Omittable<{
  /** The key `useDrawerPages().open` pushes this page by. */
  value: string;
  title: React.ReactNode;
  /** The header's trailing slot — a Clear, a Done. */
  action?: React.ReactNode;
  /** The back chevron's accessible name. */
  backLabel?: string;
  /** The page's body. Wrap it in a `DrawerPanel`, as the root does. */
  children?: React.ReactNode;
  className?: string;
}>;

/**
 * One page of a `DrawerPages` stack. Render it where its content's data
 * lives; it mounts nothing until its `value` is the stack's open page, then
 * slides in over the root with a header of its own: the back chevron, the
 * title, and the trailing `action`.
 *
 * Escape inside the page pops it rather than closing the drawer — the drawer
 * hears Escape only from the root.
 */
export function DrawerPage({
  value,
  title,
  action,
  backLabel = "Back",
  children,
  className,
}: DrawerPageProps): React.ReactElement | null {
  const stack = useContext(DrawerPagesContext);
  const reduceMotion = usePrefersReducedMotion();
  const active = stack?.page === value;
  const [phase, setPhase] = useState<PagePhase>(() =>
    active ? "entering" : "closed",
  );
  const pageRef = useRef<HTMLElement | null>(null);
  const backRef = useRef<HTMLButtonElement | null>(null);

  // The phase follows `active` in render, so no frame shows a page whose
  // stack has already moved on. Reduced motion skips both slides.
  if (active && (phase === "closed" || phase === "ending")) {
    setPhase(reduceMotion ? "open" : "entering");
  } else if (!active && (phase === "open" || phase === "entering")) {
    setPhase(reduceMotion ? "closed" : "ending");
  }

  // The enter: one frame at the trailing edge, then the slide. The layout
  // read commits the starting style before the frame that removes it, or
  // the two styles land in one recalc and nothing moves. The timer backs the
  // frame up: a tab the browser has throttled may hold its frames for
  // seconds, and the page must not wait at the edge for one.
  useEffect(() => {
    if (phase !== "entering") return;
    pageRef.current?.getBoundingClientRect();
    const stand = () => setPhase("open");
    const frame = requestAnimationFrame(stand);
    const timer = window.setTimeout(stand, ENTER_FRAME_FALLBACK_MS);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [phase]);

  // The end: the slide out, then unmount — on `transitionend`, or on the
  // fallback timer when no transition runs to completion.
  useEffect(() => {
    if (phase !== "ending") return;
    const timer = window.setTimeout(
      () => setPhase("closed"),
      pageTransitionMs() + TRANSITION_END_GRACE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [phase]);

  // Focus lands on the back chevron once the page stands, unless the body
  // has already seated it (a search field that autofocuses).
  useEffect(() => {
    if (phase !== "open") return;
    const page = pageRef.current;
    if (page && !page.contains(document.activeElement)) {
      backRef.current?.focus({ preventScroll: true });
    }
  }, [phase]);

  if (phase === "closed" || !stack?.host) return null;

  return createPortal(
    <section
      aria-label={typeof title === "string" ? title : undefined}
      // The fill is the popup's own `--drawer-surface`, inherited through
      // the layer: the page is opaque over the root it slides across.
      className={cn(
        "absolute inset-0 flex flex-col bg-(--drawer-surface) transition-[translate] duration-slow ease-drawer data-ending-style:translate-x-full data-starting-style:translate-x-full motion-reduce:transition-none",
        className,
      )}
      data-ending-style={phase === "ending" || undefined}
      data-slot="drawer-page"
      data-starting-style={phase === "entering" || undefined}
      onKeyDown={(event) => {
        if (event.key !== "Escape" || phase !== "open") return;
        event.stopPropagation();
        stack.back();
      }}
      onTransitionEnd={(event) => {
        if (event.target === event.currentTarget && phase === "ending") {
          setPhase("closed");
        }
      }}
      ref={pageRef}
    >
      <DrawerHeader className="flex-row items-center gap-1">
        {/* Pulled back so the chevron's glyph sits on the header's inset
            where the root's title starts, not its 28px hit box. */}
        <Button
          aria-label={backLabel}
          className="-ms-1.5"
          onClick={stack.back}
          ref={backRef}
          size="icon-xs"
          variant="ghost"
        >
          <Icon name="chevron-left" />
        </Button>
        {/* The 28px line holds the row at the `sm` button rung whether or
            not an action is mounted, so a Clear that appears once a value is
            set does not push the title down by the difference between its
            height and the chevron's. */}
        <h2
          className="min-w-0 flex-1 truncate font-heading font-semibold text-xl leading-7"
          data-slot="drawer-page-title"
        >
          {title}
        </h2>
        {action ? (
          <div className="flex shrink-0 items-center" data-slot="drawer-page-action">
            {action}
          </div>
        ) : null}
      </DrawerHeader>
      {children}
    </section>,
    stack.host,
  );
}
